import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Section, SectionHeader } from "../components/Section";
import { CTA } from "../components/CTA";
import { CTASection } from "../components/CTASection";
import {
  LICENSE_TIERS,
  LICENSING_AUDIENCES,
  PRICING_SECTIONS,
  GROUP_SEAT_PLANS,
  type LicenseTier,
} from "../lib/licensing";
import { useIdentity } from "../lib/identity";
import { createCheckoutSession } from "../lib/stripe-api";
import { useState } from "react";
import { FAQSection } from "../components/FAQSection";
import { LICENSING_FAQS } from "../lib/faq-content";
import { useTranslation } from "../lib/i18n";

function TierIcon({ name, size = 24 }: { name: string; size?: number }) {
  const props = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "portal":
      return (
        <svg {...props}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18" />
          <path d="M9 21V9" />
        </svg>
      );
    case "architecture":
      return (
        <svg {...props}>
          <path d="M3 21h18" />
          <path d="M5 21V7l8-4v18" />
          <path d="M19 21V11l-6-4" />
          <path d="M9 9v.01" />
          <path d="M9 12v.01" />
          <path d="M9 15v.01" />
          <path d="M9 18v.01" />
        </svg>
      );
    case "template":
      return (
        <svg {...props}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18" />
          <path d="M9 16l2 2 4-4" />
        </svg>
      );
    case "certification":
      return (
        <svg {...props}>
          <circle cx="12" cy="8" r="6" />
          <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
        </svg>
      );
    case "automation":
      return (
        <svg {...props}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <path d="M9 9h6v6H9z" />
          <path d="M9 1v3" />
          <path d="M15 1v3" />
          <path d="M9 20v3" />
          <path d="M15 20v3" />
          <path d="M20 9h3" />
          <path d="M20 14h3" />
          <path d="M1 9h3" />
          <path d="M1 14h3" />
        </svg>
      );
    case "vendor":
      return (
        <svg {...props}>
          <path d="M4 7V4a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v3" />
          <path d="M4 17v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
          <rect x="3" y="7" width="18" height="10" rx="1" />
          <path d="M7 12h.01" />
          <path d="M12 12h.01" />
          <path d="M17 12h.01" />
        </svg>
      );
    case "organization":
      return (
        <svg {...props}>
          <path d="M3 21h18" />
          <path d="M5 21V7l7-4 7 4v14" />
          <path d="M9 21v-6h6v6" />
          <path d="M9 11h.01" />
          <path d="M15 11h.01" />
        </svg>
      );
    default:
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
  }
}

function CheckIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "0.125rem" }}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export default function LicensingPage() {
  const { identity } = useIdentity();
  const { t } = useTranslation();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  async function handleGroupCheckout(planId: string, priceId: string, licenseType: "agency" | "enterprise") {
    if (!identity.isAuthenticated) {
      window.location.href = `/login?redirect=licensing`;
      return;
    }
    if (!identity.userId) {
      setCheckoutError("Unable to identify your account. Please log in again.");
      return;
    }
    setCheckoutLoading(planId);
    setCheckoutError(null);
    const session = await createCheckoutSession({
      user_id: identity.userId,
      license_type: licenseType,
      price_plan_id: priceId,
      success_url: `${window.location.origin}/dashboard?checkout=success&plan=${planId}`,
      cancel_url: `${window.location.origin}/licensing?checkout=cancelled`,
    });
    setCheckoutLoading(null);
    if (session?.url) {
      window.location.href = session.url;
    } else {
      setCheckoutError("Unable to start checkout. Please try again or contact support.");
    }
  }

  async function handleTierPurchase(tierId: string, priceLabel: string, price: number) {
    if (!identity.isAuthenticated) {
      window.location.href = `/register?redirect=licensing`;
      return;
    }
    if (!identity.userId) {
      setCheckoutError("Unable to identify your account. Please log in again.");
      return;
    }
    const btnKey = `${tierId}-${priceLabel}`;
    setCheckoutLoading(btnKey);
    setCheckoutError(null);

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/licensing-quote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          action: "create_license_checkout",
          user_id: identity.userId,
          tier_id: tierId,
          price_label: priceLabel,
          price,
          success_url: `${window.location.origin}/dashboard?checkout=success&tier=${tierId}`,
          cancel_url: `${window.location.origin}/licensing?checkout=cancelled`,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error ?? `Request failed (${res.status})`);
      }
      const data = await res.json();
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned.");
      }
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : "Unable to start checkout. Please try again or contact support.");
    } finally {
      setCheckoutLoading(null);
    }
  }

  return (
    <>
      <SEO
        title="Licensing — Sentinel"
        description="Sentinel licensing tiers: Use, Architecture, Template, Certification, Automation, Vendor Integration, and Organizational Certification. Public pricing for individuals and teams; quote-based enterprise pricing."
        canonical="/licensing"
      />
      <PageHero
        eyebrow={t("licensingFramework.eyebrow")}
        title={t("licensingFramework.title")}
        description={t("licensingFramework.description")}
        bgVariant="burgundy"
        bgImage="https://images.pexels.com/photos/6814522/pexels-photo-6814522.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />

      {/* Why Licensing */}
      <Section>
        <SectionHeader
          eyebrow={t("licensingFramework.whyEyebrow")}
          title={t("licensingFramework.whyTitle")}
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1rem" }}>
          {[
            t("licensingFramework.why1"),
            t("licensingFramework.why2"),
            t("licensingFramework.why3"),
            t("licensingFramework.why4"),
            t("licensingFramework.why5"),
            t("licensingFramework.why6"),
            t("licensingFramework.why7"),
          ].map((item) => (
            <div key={item} className="card" style={{ padding: "1.25rem", display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
              <span style={{ color: "var(--accent-600)", flexShrink: 0 }}>
                <CheckIcon size={18} />
              </span>
              <span style={{ fontSize: "0.875rem", lineHeight: 1.5 }}>{item}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Licensing Tiers */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow={t("licensingFramework.tiersEyebrow")}
          title={t("licensingFramework.tiersTitle")}
          description={t("licensingFramework.tiersDescription")}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          {LICENSE_TIERS.map((tier) => (
            <TierCard key={tier.id} tier={tier} identity={identity} checkoutLoading={checkoutLoading} onPurchase={handleTierPurchase} />
          ))}
        </div>
      </Section>

      {/* How Licensing Works */}
      <Section>
        <SectionHeader
          eyebrow={t("licensingFramework.howEyebrow")}
          title={t("licensingFramework.howTitle")}
          description={t("licensingFramework.howDescription")}
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1.25rem" }}>
          {LICENSING_AUDIENCES.map((aud) => (
            <div key={aud.title} className="card" style={{ padding: "1.5rem" }}>
              <h3 style={{ fontSize: "1rem", marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ color: "var(--accent-600)" }}>
                  <CheckIcon size={18} />
                </span>
                {aud.title}
              </h3>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {aud.items.map((item) => (
                  <li key={item} style={{ fontSize: "0.8125rem", color: "var(--text-muted)", display: "flex", gap: "0.5rem", lineHeight: 1.5 }}>
                    <span style={{ color: "var(--accent-500)", flexShrink: 0 }}>{"\u2022"}</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* Pricing Strategy */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow={t("licensingFramework.pricingEyebrow")}
          title={t("licensingFramework.pricingTitle")}
          description={t("licensingFramework.pricingDescription")}
        />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.5rem" }}>
          {PRICING_SECTIONS.map((section) => (
            <div key={section.title} className="card" style={{ padding: "1.5rem", display: "flex", flexDirection: "column" }}>
              <h3 style={{ fontSize: "1.0625rem", marginBottom: "0.5rem" }}>{section.title}</h3>
              <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5, marginBottom: "0.75rem" }}>{section.description}</p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.375rem", flex: 1 }}>
                {section.items.map((item) => (
                  <li key={item} style={{ display: "flex", gap: "0.5rem", fontSize: "0.8125rem", lineHeight: 1.5 }}>
                    <span style={{ color: "var(--accent-500)", flexShrink: 0 }}>
                      <CheckIcon size={14} />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <CTA to={section.ctaTo} variant={section.isQuoteBased ? "secondary" : "primary"} style={{ marginTop: "1rem", fontSize: "0.8125rem" }}>
                {section.ctaLabel}
              </CTA>
            </div>
          ))}
        </div>
      </Section>

      {/* Group Seat Plans */}
      <Section>
        <SectionHeader
          eyebrow={t("licensingFramework.groupEyebrow")}
          title={t("licensingFramework.groupTitle")}
          description={t("licensingFramework.groupDescription")}
        />

        {checkoutError && (
          <div className="card" style={{ padding: "1rem 1.5rem", marginBottom: "1rem", borderColor: "var(--error-400)", background: "var(--error-50)", color: "var(--error-700)", fontWeight: 600, fontSize: "0.875rem" }}>
            {checkoutError}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1rem" }}>
          {GROUP_SEAT_PLANS.map((plan) => (
            <div
              key={plan.id}
              className="card card-hover"
              style={{
                padding: "1.5rem",
                display: "flex",
                flexDirection: "column",
                borderColor: plan.popular ? "var(--accent-400)" : "var(--border)",
                position: "relative",
              }}
            >
              {plan.popular && (
                <span className="badge badge-accent" style={{ position: "absolute", top: "0.75rem", right: "0.75rem" }}>
                  {t("licensingFramework.mostPopular")}
                </span>
              )}
              <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--accent-600)", marginBottom: "0.5rem" }}>
                {plan.type === "enterprise" ? t("licensingFramework.enterprise") : t("licensingFramework.agency")}
              </div>
              <h3 style={{ fontSize: "1.125rem", marginBottom: "0.5rem" }}>{plan.name}</h3>
              <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "0.75rem" }}>{plan.description}</p>
              <div style={{ fontSize: "1.75rem", fontWeight: 800, marginBottom: "0.75rem" }}>
                ${plan.price.toLocaleString()}<span style={{ fontSize: "0.75rem", fontWeight: 400, color: "var(--text-muted)" }}> {t("licensingFramework.perYear")}</span>
              </div>
              <ul style={{ display: "flex", flexDirection: "column", gap: "0.375rem", marginBottom: "1rem", flex: 1 }}>
                {plan.features.map((f) => (
                  <li key={f} style={{ display: "flex", gap: "0.5rem", fontSize: "0.8125rem" }}>
                    <span style={{ color: "var(--accent-500)", flexShrink: 0 }}>
                      <CheckIcon size={14} />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleGroupCheckout(plan.id, plan.priceId, plan.type)}
                disabled={checkoutLoading === plan.id}
                className={plan.popular ? "btn btn-primary" : "btn btn-secondary"}
                style={{ width: "100%", fontSize: "0.8125rem" }}
              >
                {checkoutLoading === plan.id ? t("licensingFramework.redirecting") : identity.isAuthenticated ? `${t("licensingFramework.purchase")} ${plan.name}` : t("licensingFramework.loginToPurchase")}
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* Why Hybrid Pricing */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow={t("licensingFramework.hybridEyebrow")}
          title={t("licensingFramework.hybridTitle")}
          description={t("licensingFramework.hybridDescription")}
        />
        <div className="card" style={{ padding: "1.5rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1rem" }}>
            <div>
              <h4 style={{ fontSize: "0.9375rem", marginBottom: "0.5rem" }}>{t("licensingFramework.whatsPublic")}</h4>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.375rem" }}>
                {[
                  t("licensingFramework.public1"),
                  t("licensingFramework.public2"),
                  t("licensingFramework.public3"),
                ].map((item) => (
                  <li key={item} style={{ display: "flex", gap: "0.5rem", fontSize: "0.8125rem", lineHeight: 1.5 }}>
                    <span style={{ color: "var(--accent-500)", flexShrink: 0 }}>
                      <CheckIcon size={14} />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 style={{ fontSize: "0.9375rem", marginBottom: "0.5rem" }}>{t("licensingFramework.whatsQuoteBased")}</h4>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.375rem" }}>
                {[
                  t("licensingFramework.quote1"),
                  t("licensingFramework.quote2"),
                  t("licensingFramework.quote3"),
                ].map((item) => (
                  <li key={item} style={{ display: "flex", gap: "0.5rem", fontSize: "0.8125rem", lineHeight: 1.5 }}>
                    <span style={{ color: "var(--primary-500)", flexShrink: 0 }}>{"\u25CF"}</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 style={{ fontSize: "0.9375rem", marginBottom: "0.5rem" }}>{t("licensingFramework.whyQuoteBased")}</h4>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.375rem" }}>
                {[
                  t("licensingFramework.whyQuote1"),
                  t("licensingFramework.whyQuote2"),
                  t("licensingFramework.whyQuote3"),
                  t("licensingFramework.whyQuote4"),
                  t("licensingFramework.whyQuote5"),
                ].map((item) => (
                  <li key={item} style={{ display: "flex", gap: "0.5rem", fontSize: "0.8125rem", lineHeight: 1.5 }}>
                    <span style={{ color: "var(--accent-500)", flexShrink: 0 }}>
                      <CheckIcon size={14} />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      <CTASection
        title={t("licensingFramework.ctaTitle")}
        description={t("licensingFramework.ctaDescription")}
        primaryLabel={identity.isAuthenticated ? t("licensingFramework.ctaViewCerts") : t("licensingFramework.ctaRegister")}
        primaryTo={identity.isAuthenticated ? "/certifications" : "/register"}
        secondaryLabel={t("licensingFramework.ctaContactSales")}
        secondaryTo="/contact"
      />

      <LicensingFAQ />
    </>
  );
}

interface TierCardProps {
  tier: LicenseTier;
  identity: { isAuthenticated: boolean; userId: string | null };
  checkoutLoading: string | null;
  onPurchase: (tierId: string, priceLabel: string, price: number) => void;
}

function TierCard({ tier, identity, checkoutLoading, onPurchase }: TierCardProps) {
  const { t } = useTranslation();
  const isOrg = tier.id === "organizational";
  const accentColor = isOrg ? "var(--primary-600)" : "var(--accent-600)";
  const publicPricings = tier.pricing.filter((p) => p.mode === "public" && p.price !== null);

  return (
    <div
      className="card"
      style={{
        padding: "2rem",
        borderLeft: `4px solid ${accentColor}`,
        background: isOrg ? "var(--primary-50, #eff6ff)" : undefined,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem", marginBottom: "1rem" }}>
        <div
          style={{
            width: "3rem",
            height: "3rem",
            borderRadius: "var(--radius-md)",
            background: `${accentColor}12`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: accentColor,
            flexShrink: 0,
          }}
        >
          <TierIcon name={tier.icon} size={24} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
            {tier.number && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "1.5rem",
                  height: "1.5rem",
                  borderRadius: "50%",
                  background: accentColor,
                  color: "white",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                }}
              >
                {tier.number}
              </span>
            )}
            {isOrg && (
              <span
                style={{
                  padding: "0.125rem 0.5rem",
                  borderRadius: "var(--radius-sm)",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  background: "var(--primary-100, #dbeafe)",
                  color: "var(--primary-700, #1d4ed8)",
                }}
              >
                {t("licensingFramework.highestLevel")}
              </span>
            )}
          </div>
          <h3 style={{ fontSize: "1.25rem", marginBottom: "0.25rem" }}>{tier.name}</h3>
          <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: 0 }}>{tier.tagline}</p>
        </div>
      </div>

      <p style={{ fontSize: "0.875rem", lineHeight: 1.6, marginBottom: "1.25rem", color: "var(--text-body)" }}>
        {tier.description}
      </p>

      {/* Real-world scenario */}
      <div
        style={{
          background: "var(--bg-subtle, #f8fafc)",
          borderRadius: "var(--radius-md)",
          padding: "1rem 1.25rem",
          marginBottom: "1.25rem",
          borderLeft: "3px solid var(--accent-400, #34d399)",
        }}
      >
        <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--accent-600)", marginBottom: "0.375rem" }}>
          {t("licensingFramework.realWorldExample")}
        </div>
        <div style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--text-body)", marginBottom: "0.375rem" }}>
          {tier.scenario.title}
        </div>
        <p style={{ fontSize: "0.8125rem", lineHeight: 1.6, color: "var(--text-muted)", margin: 0 }}>
          {tier.scenario.body}
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: "1.5rem" }}>
        {/* Covers */}
        <div>
          <h4 style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: "0.5rem" }}>{t("licensingFramework.covers")}</h4>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.375rem" }}>
            {tier.covers.map((item) => (
              <li key={item} style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontSize: "0.8125rem", lineHeight: 1.5 }}>
                <span style={{ color: "var(--accent-600)", flexShrink: 0 }}>
                  <CheckIcon size={14} />
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Pricing + Who needs it */}
        <div>
          <h4 style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: "0.5rem" }}>{t("licensingFramework.pricing")}</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "0.75rem" }}>
            {tier.pricing.map((p) => (
              <div
                key={p.label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "0.5rem 0.75rem",
                  borderRadius: "var(--radius-sm)",
                  background: p.mode === "quote" ? "var(--bg-subtle, #f8fafc)" : "var(--accent-50, #ecfdf5)",
                  border: `1px solid ${p.mode === "quote" ? "var(--border)" : "var(--accent-200, #a7f3d0)"}`,
                }}
              >
                <span style={{ fontSize: "0.8125rem", fontWeight: 500 }}>{p.label}</span>
                {p.price !== null ? (
                  <span style={{ fontSize: "0.875rem", fontWeight: 700, color: "var(--accent-700, #047857)" }}>
                    ${p.price.toLocaleString()}{p.period}
                  </span>
                ) : (
                  <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--text-muted)", fontStyle: "italic" }}>
                    {t("licensingFramework.quoteBased")}
                  </span>
                )}
              </div>
            ))}
          </div>

          <div style={{ marginBottom: "0.75rem" }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: "0.25rem" }}>{t("licensingFramework.whoNeedsIt")}</div>
            <p style={{ fontSize: "0.8125rem", lineHeight: 1.5, margin: 0 }}>{tier.whoNeedsIt}</p>
          </div>

          {tier.priceFactors.length > 1 && (
            <div>
              <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: "0.25rem" }}>{t("licensingFramework.whatDeterminesPrice")}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
                {tier.priceFactors.map((f) => (
                  <span
                    key={f}
                    style={{
                      padding: "0.2rem 0.5rem",
                      borderRadius: "var(--radius-full)",
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      background: "var(--bg-subtle, #f8fafc)",
                      color: "var(--text-muted)",
                      border: "1px solid var(--border)",
                    }}
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: "1.5rem", paddingTop: "1.5rem", borderTop: "1px solid var(--border)" }}>
        {publicPricings.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "center" }}>
            {publicPricings.map((p) => {
              const btnKey = `${tier.id}-${p.label}`;
              return (
                <button
                  key={btnKey}
                  onClick={() => onPurchase(tier.id, p.label, p.price!)}
                  disabled={checkoutLoading === btnKey}
                  className="btn btn-primary"
                  style={{ fontSize: "0.8125rem", flex: "1 1 auto", minWidth: "200px" }}
                >
                  {checkoutLoading === btnKey ? "Redirecting..." : identity.isAuthenticated ? `Purchase ${p.label} — ${p.price!.toLocaleString()}/yr` : `Log In to Purchase ${p.label}`}
                </button>
              );
            })}
            {tier.pricing.some((p) => p.mode === "quote") && (
              <CTA to={tier.ctaTo} variant="secondary" style={{ fontSize: "0.8125rem" }}>
                {t("licensingFramework.requestQuoteOrg")}
              </CTA>
            )}
          </div>
        ) : (
          <CTA to={tier.ctaTo} variant={isOrg ? "primary" : "secondary"} style={{ fontSize: "0.875rem" }}>
            {tier.ctaLabel}
          </CTA>
        )}
      </div>
    </div>
  );
}

function LicensingFAQ() {
  const { t } = useTranslation();
  return <FAQSection items={LICENSING_FAQS} title={t("licensingFramework.faqTitle")} />;
}
